import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { authService } from "@/services/authService";
import { useAuthState } from "@/hooks/useAuthState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User } from "lucide-react";
import UserPhotoUpload from "@/components/UserPhotoUpload";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import { CountrySelect, Country } from "@/components/CountrySelect";
import InputMask from "react-input-mask";

const formatCpfForDisplay = (raw: string | undefined) => {
  if (!raw) return "";
  const d = String(raw).replace(/\D/g, '');
  if (d.length < 11) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

const EditProfile = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneDdi, setPhoneDdi] = useState("55");
  const [cpf, setCpf] = useState("");
  const [address, setAddress] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const { toast } = useToast();
  const { user, userId, refreshUser } = useAuthState();

  useEffect(() => {
    const loadUserData = async () => {
      if (userId) {
        setLoadingData(true);
        try {
          // Buscar dados atualizados do usuário diretamente do banco
          const currentUser = await authService.getUserById(userId);
          if (currentUser) {
            setName(currentUser.name || "");
            setEmail(currentUser.email || "");
            setPhone(currentUser.phone || "");
            setPhoneDdi((currentUser as any).phone_ddi || "55");
            setProfilePhoto(currentUser.profile_photo || "");
            setCpf(formatCpfForDisplay((currentUser as any).cpf));
            setAddress((currentUser as any).address || "");
            setBirthDate((currentUser as any).birth_date ? String((currentUser as any).birth_date).slice(0, 10) : "");
            console.log('📋 Dados do usuário carregados:', currentUser);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar dados do usuário:', error);
          // Fallback para dados do estado de autenticação
          if (user) {
            setName(user.name || "");
            setEmail(user.email || "");
            setPhone(user.phone || "");
            setPhoneDdi((user as any).phone_ddi || "55");
            setProfilePhoto(user.profile_photo || "");
            setCpf(formatCpfForDisplay((user as any).cpf));
            setAddress((user as any).address || "");
            setBirthDate((user as any).birth_date ? String((user as any).birth_date).slice(0, 10) : "");
          }
        } finally {
          setLoadingData(false);
        }
      }
    };

    loadUserData();
  }, [userId, user]);

  const handlePhotoUpdate = (filename: string) => {
    setProfilePhoto(filename);
    // Atualizar o estado de autenticação para refletir a mudança
    refreshUser();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔄 EditProfile.handleSubmit chamado:', { userId, name, phone });
    
    if (!userId) {
      console.log('❌ Usuário não encontrado');
      toast({
        title: "Erro",
        description: "Usuário não encontrado",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      console.log('📞 Chamando authService.updateUser...');
      const cpfDigits = cpf.replace(/\D/g, '');
      const updatedUser = await authService.updateUser(userId, {
        name,
        phone,
        phone_ddi: phoneDdi,
        cpf: cpfDigits || undefined,
        address: address.trim() || undefined,
        birth_date: birthDate || undefined,
      });
      console.log('✅ Usuário atualizado:', updatedUser);
      
      if (updatedUser) {
        // Atualizar o estado local com os novos dados
        setName(updatedUser.name || "");
        setPhone(updatedUser.phone || "");
        
        // O evento customizado 'userUpdated' será disparado automaticamente
        // e o useAuthState será atualizado, incluindo o header
        
        toast({
          title: "Perfil atualizado",
          description: "Suas informações foram atualizadas com sucesso!",
        });
      } else {
        throw new Error("Falha ao atualizar o perfil");
      }
    } catch (error: unknown) {
      console.error('❌ Erro ao atualizar perfil:', error);
      toast({
        title: "Erro ao atualizar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Meus Dados</h1>
          <p className="text-muted-foreground">Gerencie as informações da sua conta</p>
    </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando dados do perfil...</p>
    </div>
    </div>
    </div>
    );
  }

  return (
    <MinhaAreaPageShell>
<div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Meus Dados</h1>
          <p className="text-muted-foreground">Gerencie as informações da sua conta</p>
    </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl">
        {/* Card de Upload de Foto */}
        <UserPhotoUpload
          userId={userId || 0}
          currentPhoto={profilePhoto}
          onPhotoUpdate={handlePhotoUpdate}
        />

        {/* Card de Informações */}
        <Card className="shadow-md">
          <CardHeader className="bg-muted/30">
            <CardTitle className="flex items-center gap-3 text-foreground">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User className="h-5 w-5 text-primary" />
    </div>
              Informações do Perfil
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="name" className="text-sm font-medium text-foreground">Nome</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome completo"
                required
                className="h-11"
              />
    </div>
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="h-11 bg-muted"
              />
              <p className="text-sm text-muted-foreground">
                O e-mail não pode ser alterado por questões de segurança.
              </p>
    </div>
            <div className="space-y-3">
              <Label htmlFor="phone" className="text-sm font-medium text-foreground">Telefone</Label>
              <div className="flex gap-2">
                <CountrySelect
                  value={phoneDdi}
                  onValueChange={(ddi: string, country: Country) => {
                    setPhoneDdi(ddi);
                  }}
                  disabled={loading}
                />
                <InputMask
                  mask="(99) 99999-9999"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                  className="flex-1"
                >
                  {(inputProps: any) => (
                    <Input
                      {...inputProps}
                      id="phone"
                      type="tel"
                      placeholder="(81) 99999-9999"
                      className="h-11 flex-1"
                    />
                  )}
                </InputMask>
              </div>
            </div>
            <div className="space-y-3">
              <Label htmlFor="cpf" className="text-sm font-medium text-foreground">CPF</Label>
              <InputMask
                mask="999.999.999-99"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                disabled={loading}
                maskChar={null}
              >
                {(inputProps: any) => (
                  <Input
                    {...inputProps}
                    id="cpf"
                    type="text"
                    placeholder="000.000.000-00"
                    className="h-11"
                  />
                )}
              </InputMask>
            </div>
            <div className="space-y-3">
              <Label htmlFor="address" className="text-sm font-medium text-foreground">Endereço</Label>
              <Textarea
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rua, número, bairro, cidade, estado, CEP"
                disabled={loading}
                className="min-h-[80px] resize-y"
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="birthDate" className="text-sm font-medium text-foreground">Data de nascimento</Label>
              <Input
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                disabled={loading}
                className="h-11"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11">
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </form>
        </CardContent>
      </Card>
        </div>
    </MinhaAreaPageShell>
  );
};

export default EditProfile;
